import { Controller } from "@hotwired/stimulus"
import { showPaymentProcessingOverlay } from "../payment_processing_overlay"

// Split payment controller for direct sales checkout
// Adapted from split_payment_controller.js with simplified flow
export default class extends Controller {
  static targets = [
    "splitToggle", "splitSection", "singleCardSection",
    "card1Section", "card2Section",
    "splitTypeSelect", "splitTypePills", "customAmountSection",
    "card1Amount", "card2Amount", "card1AmountDisplay", "card2AmountDisplay",
    "card1BadgeAmount", "card2BadgeAmount",
    "card1Installments", "card2Installments",
    "card1AmountInput", "card2AmountInput",
    "card1InstallmentsInput", "card2InstallmentsInput",
    "card1Tab", "card2Tab",
    "splitBar1", "splitBar2",
    "backButton", "inlineCaptchaSection", "inlineSubmitButton",
    "singleCardCaptcha", "singleCardSubmit"
  ]

  static values = {
    totalCents: Number,
    maxInstallments: Number,
    paymentToken: String,
    singleUrl: String,
    splitUrl: String
  }

  connect() {
    this.splitEnabled = false
    this.activeCard = 1
    this.card1Complete = false
    this.card2Complete = false
    this.autoAdvanceEnabled = true
    this.autoAdvanceDebounceTimer = null
    this.form = this.element.closest('form')
    this.updateDisplay()

    setTimeout(() => this.setupAutoAdvanceListeners(), 100)
  }

  setupAutoAdvanceListeners() {
    if (this.hasCard1SectionTarget) {
      const card1Fields = this.card1SectionTarget.querySelectorAll('input')
      card1Fields.forEach(field => {
        field.addEventListener('input', () => this.checkCard1AutoAdvance())
      })
    }

    if (this.hasCard2SectionTarget) {
      const card2Fields = this.card2SectionTarget.querySelectorAll('input')
      card2Fields.forEach(field => {
        field.addEventListener('input', () => this.checkCard2Completion())
      })
    }
  }

  checkCard1AutoAdvance() {
    if (!this.splitEnabled || !this.autoAdvanceEnabled || this.card1Complete || this.activeCard !== 1) {
      return
    }

    clearTimeout(this.autoAdvanceDebounceTimer)
    this.autoAdvanceDebounceTimer = setTimeout(() => {
      const validation = this.validateCard1Fields()
      if (validation.valid) {
        this.autoAdvanceToCard2()
      }
    }, 500)
  }

  autoAdvanceToCard2() {
    this.card1Complete = true
    this.showCard2()
  }

  checkCard2Completion() {
    if (!this.splitEnabled || this.activeCard !== 2) {
      return
    }

    const validation = this.validateCard2Fields()
    if (validation.valid && !this.card2Complete) {
      this.card2Complete = true
      this.updateTabStyles()
      this.updateActionButtons()
    } else if (!validation.valid && this.card2Complete) {
      this.card2Complete = false
      this.updateTabStyles()
      this.updateActionButtons()
    }
  }

  validateCardFields(section, prefix) {
    const errors = []
    const cardNumber = section.querySelector(`input[name="${prefix}[card_number]"]`)?.value?.replace(/\s/g, '') || ''
    const holderName = section.querySelector(`input[name="${prefix}[holder_name]"]`)?.value?.trim() || ''
    const expiration = section.querySelector(`input[name="${prefix}[card_expiration]"]`)?.value?.trim() || ''
    const cvv = section.querySelector(`input[name="${prefix}[card_cvv]"]`)?.value?.trim() || ''

    if (cardNumber.length < 13 || cardNumber.length > 19) {
      errors.push({ field: 'card_number', message: 'Numero do cartao invalido' })
    }
    if (holderName.length < 3) {
      errors.push({ field: 'holder_name', message: 'Nome no cartao e obrigatorio' })
    }
    if (!/^\d{2}\/\d{2}$/.test(expiration)) {
      errors.push({ field: 'card_expiration', message: 'Validade invalida' })
    }
    if (!/^\d{3,4}$/.test(cvv)) {
      errors.push({ field: 'card_cvv', message: 'CVV invalido' })
    }

    return { valid: errors.length === 0, errors }
  }

  validateCard1Fields() {
    return this.validateCardFields(this.card1SectionTarget, 'card1_profile')
  }

  validateCard2Fields() {
    return this.validateCardFields(this.card2SectionTarget, 'card2_profile')
  }

  toggleSplitPayment(event) {
    this.splitEnabled = event.target.checked
    this.card1Complete = false
    this.card2Complete = false
    clearTimeout(this.autoAdvanceDebounceTimer)

    if (this.splitEnabled) {
      this.splitSectionTarget.classList.remove("hidden")
      this.singleCardSectionTarget.classList.add("hidden")
      this.updateSplitAmounts()

      if (this.form) {
        this.form.dataset.splitPaymentEnabled = 'true'
      }

      // Disable single card fields to prevent validation errors on hidden fields
      this.toggleSingleCardFields(true)

      // Hide single card captcha/submit
      if (this.hasSingleCardCaptchaTarget) {
        this.singleCardCaptchaTarget.classList.add("hidden")
      }
      if (this.hasSingleCardSubmitTarget) {
        this.singleCardSubmitTarget.classList.add("hidden")
      }

      this.showCard1Instant()
      this.scrollToCardSection()
    } else {
      this.splitSectionTarget.classList.add("hidden")
      this.singleCardSectionTarget.classList.remove("hidden")

      if (this.form) {
        delete this.form.dataset.splitPaymentEnabled
      }

      // Re-enable single card fields
      this.toggleSingleCardFields(false)

      // Show single card captcha/submit
      if (this.hasSingleCardCaptchaTarget) {
        this.singleCardCaptchaTarget.classList.remove("hidden")
      }
      if (this.hasSingleCardSubmitTarget) {
        this.singleCardSubmitTarget.classList.remove("hidden")
      }

      if (this.hasInlineCaptchaSectionTarget) {
        this.inlineCaptchaSectionTarget.classList.add('hidden')
      }

      this.scrollToCardSection()
    }

    this.updateFormAction()
  }

  // Disable/enable single card fields to prevent validation errors when hidden
  toggleSingleCardFields(disable) {
    if (this.hasSingleCardSectionTarget) {
      if (disable) {
        // When disabling, find fields with required attribute
        const fields = this.singleCardSectionTarget.querySelectorAll('input[required], select[required]')
        fields.forEach(field => {
          field.dataset.wasRequired = 'true'
          field.removeAttribute('required')
          field.disabled = true
        })
      } else {
        // When enabling, find fields that WERE required (marked with data attribute)
        const fields = this.singleCardSectionTarget.querySelectorAll('input[data-was-required="true"], select[data-was-required="true"]')
        fields.forEach(field => {
          field.setAttribute('required', 'required')
          delete field.dataset.wasRequired
          field.disabled = false
        })
      }
    }
  }

  scrollToCardSection() {
    setTimeout(() => {
      const parentCard = this.element.closest('.bg-white.rounded-2xl')
      if (parentCard) {
        const yOffset = -20
        const y = parentCard.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    }, 100)
  }

  updateFormAction() {
    if (this.form) {
      if (this.splitEnabled) {
        this.form.action = this.splitUrlValue || `/venda-direta/pagamento/${this.paymentTokenValue}/processar-parcelado`
        this.addHiddenField('is_split_payment', 'true')
      } else {
        this.form.action = this.singleUrlValue || `/venda-direta/pagamento/${this.paymentTokenValue}/processar`
        this.removeHiddenField('is_split_payment')
      }
    }
  }

  addHiddenField(name, value) {
    let field = this.form.querySelector(`input[name="${name}"]`)
    if (!field) {
      field = document.createElement('input')
      field.type = 'hidden'
      field.name = name
      this.form.appendChild(field)
    }
    field.value = value
  }

  removeHiddenField(name) {
    const field = this.form.querySelector(`input[name="${name}"]`)
    if (field) {
      field.remove()
    }
  }

  selectSplitTypePill(event) {
    const button = event.currentTarget
    const splitType = button.dataset.splitType

    if (this.hasSplitTypeSelectTarget) {
      this.splitTypeSelectTarget.value = splitType
    }

    const pills = this.splitTypePillsTarget.querySelectorAll('.split-pill')
    pills.forEach(pill => {
      pill.classList.remove('border-green-500', 'bg-green-50', 'text-green-700')
      pill.classList.add('border-gray-200', 'bg-white', 'text-gray-600')
    })
    button.classList.remove('border-gray-200', 'bg-white', 'text-gray-600')
    button.classList.add('border-green-500', 'bg-green-50', 'text-green-700')

    if (splitType === "custom") {
      this.customAmountSectionTarget.classList.remove("hidden")
      const card1Amount = parseInt(this.card1AmountInputTarget.value) || 0
      const card2Amount = parseInt(this.card2AmountInputTarget.value) || 0
      if (this.hasCard1AmountTarget) {
        this.card1AmountTarget.value = this.formatMoney(card1Amount)
      }
      if (this.hasCard2AmountTarget) {
        this.card2AmountTarget.value = this.formatMoney(card2Amount)
      }
    } else {
      this.customAmountSectionTarget.classList.add("hidden")
      this.applySplitType(splitType)
    }

    this.updateDisplay()
    this.updateSplitBar()
  }

  showCard1Instant() {
    this.activeCard = 1
    this.card1SectionTarget.classList.remove("hidden")
    this.card2SectionTarget.classList.add("hidden")
    this.updateTabStyles()
    this.updateActionButtons()
  }

  showCard1() {
    if (this.activeCard === 1) return
    this.activeCard = 1
    this.animateTransition(this.card2SectionTarget, this.card1SectionTarget, 'right')
    this.updateTabStyles()
    this.updateActionButtons()
  }

  showCard2() {
    if (this.activeCard === 2) return
    this.activeCard = 2
    this.animateTransition(this.card1SectionTarget, this.card2SectionTarget, 'left')
    this.updateTabStyles()
    this.updateActionButtons()
  }

  animateTransition(fromSection, toSection, direction) {
    const duration = 200

    fromSection.style.transition = `opacity ${duration}ms ease-out`
    fromSection.style.opacity = '0'

    setTimeout(() => {
      fromSection.classList.add('hidden')
      fromSection.style.transition = ''
      fromSection.style.opacity = ''

      toSection.classList.remove('hidden')
      toSection.style.opacity = '0'
      toSection.style.transition = `opacity ${duration}ms ease-in`

      requestAnimationFrame(() => {
        toSection.style.opacity = '1'
      })

      setTimeout(() => {
        toSection.style.transition = ''
        toSection.style.opacity = ''
      }, duration + 50)
    }, duration)
  }

  validateAndShowCard2() {
    const validation = this.validateCard1Fields()

    if (!validation.valid) {
      this.showValidationErrors(validation.errors, this.card1SectionTarget, 'card1_profile')
      return
    }

    this.card1Complete = true
    this.updateTabStyles()
    this.showCard2()
  }

  showValidationErrors(errors, section, prefix) {
    section.querySelectorAll('.border-red-300').forEach(el => {
      el.classList.remove('border-red-300')
    })
    section.querySelectorAll('.validation-error').forEach(el => el.remove())

    errors.forEach(error => {
      const input = section.querySelector(`input[name="${prefix}[${error.field}]"]`)
      if (input) {
        input.classList.add('border-red-300')
        const errorEl = document.createElement('p')
        errorEl.className = 'mt-1 text-sm text-red-600 validation-error'
        errorEl.textContent = error.message
        input.parentNode.appendChild(errorEl)
      }
    })

    const firstError = section.querySelector('.border-red-300')
    if (firstError) {
      firstError.focus()
    }
  }

  updateTabStyles() {
    if (!this.hasCard1TabTarget || !this.hasCard2TabTarget) return

    const classes = [
      'bg-white', 'bg-green-50', 'shadow-sm',
      'text-gray-800', 'text-green-600', 'text-gray-500',
      'font-semibold', 'font-medium'
    ]
    classes.forEach(c => {
      this.card1TabTarget.classList.remove(c)
      this.card2TabTarget.classList.remove(c)
    })

    if (this.activeCard === 1) {
      this.card1TabTarget.classList.add('bg-white', 'shadow-sm', 'text-gray-800', 'font-semibold')
    } else if (this.card1Complete) {
      this.card1TabTarget.classList.add('bg-green-50', 'text-green-600', 'font-medium')
    } else {
      this.card1TabTarget.classList.add('text-gray-500', 'font-medium')
    }

    if (this.activeCard === 2) {
      this.card2TabTarget.classList.add('bg-white', 'shadow-sm', 'text-gray-800', 'font-semibold')
    } else if (this.card2Complete) {
      this.card2TabTarget.classList.add('bg-green-50', 'text-green-600', 'font-medium')
    } else {
      this.card2TabTarget.classList.add('text-gray-500', 'font-medium')
    }
  }

  updateSplitBar() {
    if (!this.hasSplitBar1Target || !this.hasSplitBar2Target) return

    const total = this.totalCentsValue
    const card1Amount = parseInt(this.card1AmountInputTarget.value) || 0
    const card2Amount = parseInt(this.card2AmountInputTarget.value) || 0

    const card1Percentage = (card1Amount / total) * 100
    const card2Percentage = (card2Amount / total) * 100

    this.splitBar1Target.style.width = `${card1Percentage}%`
    this.splitBar2Target.style.width = `${card2Percentage}%`
  }

  applySplitType(splitType) {
    const total = this.totalCentsValue
    let card1Amount, card2Amount

    switch (splitType) {
      case "50_50":
        card1Amount = Math.floor(total / 2)
        card2Amount = total - card1Amount
        break
      case "60_40":
        card1Amount = Math.floor(total * 0.6)
        card2Amount = total - card1Amount
        break
      case "70_30":
        card1Amount = Math.floor(total * 0.7)
        card2Amount = total - card1Amount
        break
      default:
        card1Amount = Math.floor(total / 2)
        card2Amount = total - card1Amount
    }

    this.card1AmountInputTarget.value = card1Amount
    this.card2AmountInputTarget.value = card2Amount

    this.updateDisplay()
    this.updateSplitBar()
  }

  updateSplitAmounts() {
    this.applySplitType("50_50")
  }

  updateCustomAmount(event) {
    const total = this.totalCentsValue
    const input = event.target
    const isCard1 = this.hasCard1AmountTarget && input === this.card1AmountTarget

    if (isCard1) {
      // Cap card1 amount to total
      let card1Amount = this.parseMoneyInput(input.value)
      if (card1Amount > total) {
        card1Amount = total
        input.value = this.formatMoney(card1Amount)
      }
      const card2Amount = Math.max(0, total - card1Amount)

      this.card1AmountInputTarget.value = card1Amount
      this.card2AmountInputTarget.value = card2Amount

      if (this.hasCard2AmountTarget) {
        this.card2AmountTarget.value = this.formatMoney(card2Amount)
      }
    } else {
      // Cap card2 amount to total
      let card2Amount = this.parseMoneyInput(input.value)
      if (card2Amount > total) {
        card2Amount = total
        input.value = this.formatMoney(card2Amount)
      }
      const card1Amount = Math.max(0, total - card2Amount)

      this.card1AmountInputTarget.value = card1Amount
      this.card2AmountInputTarget.value = card2Amount

      if (this.hasCard1AmountTarget) {
        this.card1AmountTarget.value = this.formatMoney(card1Amount)
      }
    }

    this.updateDisplay()
    this.updateSplitBar()
    this.validateAmounts()
  }

  updateDisplay() {
    if (!this.splitEnabled) return

    const card1Amount = parseInt(this.card1AmountInputTarget.value) || 0
    const card2Amount = parseInt(this.card2AmountInputTarget.value) || 0

    if (this.hasCard1AmountDisplayTarget) {
      this.card1AmountDisplayTarget.textContent = this.formatMoney(card1Amount)
    }
    if (this.hasCard2AmountDisplayTarget) {
      this.card2AmountDisplayTarget.textContent = this.formatMoney(card2Amount)
    }

    if (this.hasCard1BadgeAmountTarget) {
      this.card1BadgeAmountTarget.textContent = this.formatMoney(card1Amount)
    }
    if (this.hasCard2BadgeAmountTarget) {
      this.card2BadgeAmountTarget.textContent = this.formatMoney(card2Amount)
    }

    this.updateInstallmentOptions(this.card1InstallmentsInputTarget, card1Amount)
    this.updateInstallmentOptions(this.card2InstallmentsInputTarget, card2Amount)

    this.updateSplitBar()
  }

  updateInstallmentOptions(selectElement, amountCents) {
    if (!selectElement) return

    const currentValue = selectElement.value
    const maxInstallments = this.maxInstallmentsValue || 12

    selectElement.innerHTML = ''

    for (let i = 1; i <= maxInstallments; i++) {
      const installmentValue = Math.ceil(amountCents / i)
      const option = document.createElement('option')
      option.value = i

      if (i === 1) {
        option.textContent = `1x de ${this.formatMoney(amountCents)} (a vista)`
      } else {
        option.textContent = `${i}x de ${this.formatMoney(installmentValue)}`
      }

      selectElement.appendChild(option)
    }

    selectElement.value = currentValue
  }

  parseMoneyInput(value) {
    const cleanValue = value.replace(/[R$\s.]/g, "").replace(",", ".")
    return Math.round(parseFloat(cleanValue) * 100) || 0
  }

  formatMoney(cents) {
    const value = cents / 100
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    })
  }

  validateAmounts() {
    const total = this.totalCentsValue
    const card1Amount = parseInt(this.card1AmountInputTarget.value) || 0
    const card2Amount = parseInt(this.card2AmountInputTarget.value) || 0

    return (card1Amount + card2Amount) === total && card1Amount > 0 && card2Amount > 0
  }

  handleSplitSubmit(event) {
    // Match e-commerce split_payment_controller.js EXACTLY
    if (!this.splitEnabled) return

    if (!this.validateAmounts()) {
      event.preventDefault()
      alert("A soma dos valores dos cartões deve ser igual ao total da compra")
      return
    }

    // Validate Card 1 fields
    const card1Validation = this.validateCard1Fields()
    if (!card1Validation.valid) {
      event.preventDefault()
      this.showCard1()
      this.showValidationErrors(card1Validation.errors, this.card1SectionTarget, 'card1_profile')
      return
    }

    // Validate Card 2 fields
    const card2Validation = this.validateCard2Fields()
    if (!card2Validation.valid) {
      event.preventDefault()
      this.showCard2()
      this.showValidationErrors(card2Validation.errors, this.card2SectionTarget, 'card2_profile')
      return
    }

    // Show processing overlay
    this.showProcessingOverlay()

    // Let the form submit naturally (button is type="submit")
  }

  showProcessingOverlay() {
    showPaymentProcessingOverlay()
  }

  updateActionButtons() {
    if (!this.hasBackButtonTarget) return

    if (this.card1Complete && this.card2Complete) {
      this.backButtonTarget.classList.add('hidden')

      if (this.hasInlineCaptchaSectionTarget) {
        this.inlineCaptchaSectionTarget.classList.remove('hidden')

        setTimeout(() => {
          this.inlineCaptchaSectionTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 200)
      }
    } else {
      this.backButtonTarget.classList.remove('hidden')

      if (this.hasInlineCaptchaSectionTarget) {
        this.inlineCaptchaSectionTarget.classList.add('hidden')
      }
    }
  }

  // Handle single card payment submit - show processing overlay
  handleSingleCardSubmit(event) {
    // Only handle if split payment is NOT enabled
    if (this.splitEnabled) return

    // Show processing overlay
    this.showProcessingOverlay()

    // Let the form submit naturally
  }
}
